// AdminDataManagementPage.jsx - Gestion des données admin (nettoyage, etc.)
import React, { useState } from 'react';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { 
  AlertDialog, 
  AlertDialogAction, 
  AlertDialogCancel, 
  AlertDialogContent, 
  AlertDialogDescription, 
  AlertDialogFooter, 
  AlertDialogHeader, 
  AlertDialogTitle 
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { 
  Database, 
  Trash2, 
  AlertTriangle,
  FileText,
  Users,
  Briefcase,
  CreditCard,
  Bell,
  Shield,
  Scale
} from 'lucide-react';
import api from '../lib/api';

const COLLECTIONS = [
  { name: 'cases', label: 'Dossiers', icon: Briefcase, color: 'bg-blue-500' },
  { name: 'prisoners', label: 'Détenus', icon: Users, color: 'bg-orange-500' },
  { name: 'documents', label: 'Documents', icon: FileText, color: 'bg-green-500' },
  { name: 'payment_transactions', label: 'Paiements', icon: CreditCard, color: 'bg-purple-500' },
  { name: 'notifications', label: 'Notifications', icon: Bell, color: 'bg-yellow-500' },
  { name: 'border_alerts', label: 'Alertes Frontières', icon: Shield, color: 'bg-red-500' },
  { name: 'trials', label: 'Procès', icon: Scale, color: 'bg-indigo-500' },
  { name: 'trial_documents', label: 'Documents Procès', icon: FileText, color: 'bg-cyan-500' },
  { name: 'access_shares', label: 'Partages d\'accès', icon: Users, color: 'bg-pink-500' },
  { name: 'requisitions', label: 'Réquisitions', icon: FileText, color: 'bg-teal-500' },
  { name: 'complaints', label: 'Plaintes', icon: FileText, color: 'bg-amber-500' },
  { name: 'legal_documents', label: 'Documents Juridiques', icon: FileText, color: 'bg-slate-500' },
  { name: 'interpellations', label: 'Interpellations', icon: Users, color: 'bg-sky-500' },
  { name: 'detentions', label: 'Détentions', icon: Users, color: 'bg-rose-500' }
];

const AdminDataManagementPage = () => {
  const [showClearAllDialog, setShowClearAllDialog] = useState(false);
  const [showClearCollectionDialog, setShowClearCollectionDialog] = useState(false);
  const [selectedCollection, setSelectedCollection] = useState(null);
  const [confirmationText, setConfirmationText] = useState('');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);

  const handleClearAll = async () => {
    if (confirmationText !== 'EFFACER_TOUTES_LES_DONNEES') {
      toast.error('Confirmation incorrecte');
      return;
    }

    try {
      setLoading(true);
      const response = await api.delete('/admin/clear-test-data', {
        data: { confirmation: confirmationText }
      });
      setResults(response.data.stats);
      toast.success('Toutes les données de test ont été effacées');
      setShowClearAllDialog(false);
      setConfirmationText('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    } finally {
      setLoading(false);
    }
  };

  const handleClearCollection = async () => {
    if (!selectedCollection) return;

    try {
      setLoading(true);
      const response = await api.delete(`/admin/clear-collection/${selectedCollection}`);
      toast.success(`Collection "${selectedCollection}" effacée: ${response.data.documents_deleted} documents`);
      setShowClearCollectionDialog(false);
      setSelectedCollection(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    } finally {
      setLoading(false);
    }
  };

  const openClearCollectionDialog = (collectionName) => {
    setSelectedCollection(collectionName);
    setShowClearCollectionDialog(true);
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="admin-data-management-page">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Database className="w-6 h-6" />
            Gestion des Données
          </h1>
          <p className="text-muted-foreground mt-1">
            Outils de maintenance et nettoyage de la base de données
          </p>
        </div>

        {/* Warning Card */}
        <Card className="border-destructive bg-destructive/5">
          <CardContent className="pt-6">
            <div className="flex items-start gap-4">
              <AlertTriangle className="w-8 h-8 text-destructive flex-shrink-0" />
              <div>
                <h3 className="font-semibold text-destructive">Zone Dangereuse</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  Les actions sur cette page suppriment définitivement les données. 
                  Ces opérations sont irréversibles. Assurez-vous d&apos;avoir une sauvegarde 
                  avant de procéder.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Results */}
        {results && (
          <Card className="border-green-500 bg-green-500/5">
            <CardHeader>
              <CardTitle className="text-green-600">Résultats du nettoyage</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {Object.entries(results).map(([collection, count]) => (
                  <div key={collection} className="text-center p-3 bg-background rounded-lg">
                    <p className="text-2xl font-bold">{count}</p>
                    <p className="text-xs text-muted-foreground">{collection}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Clear All Data Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Trash2 className="w-5 h-5 text-destructive" />
              Effacer toutes les données de test
            </CardTitle>
            <CardDescription>
              Supprime tous les dossiers, détenus, paiements, notifications et autres données 
              de test. Les utilisateurs et la configuration système sont conservés.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button 
              variant="destructive" 
              onClick={() => setShowClearAllDialog(true)}
              data-testid="clear-all-btn"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Effacer toutes les données de test
            </Button>
          </CardContent>
        </Card>

        {/* Individual Collections */}
        <Card>
          <CardHeader>
            <CardTitle>Effacer une collection spécifique</CardTitle>
            <CardDescription>
              Sélectionnez une collection pour effacer uniquement ses données
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {COLLECTIONS.map((collection) => {
                const Icon = collection.icon;
                return (
                  <div 
                    key={collection.name}
                    className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/50 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 ${collection.color} rounded-lg flex items-center justify-center`}>
                        <Icon className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <p className="font-medium">{collection.label}</p>
                        <p className="text-xs text-muted-foreground font-mono">{collection.name}</p>
                      </div>
                    </div>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => openClearCollectionDialog(collection.name)}
                      className="text-destructive hover:text-destructive hover:bg-destructive/10"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Clear All Dialog */}
        <AlertDialog open={showClearAllDialog} onOpenChange={setShowClearAllDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2 text-destructive">
                <AlertTriangle className="w-5 h-5" />
                Confirmer la suppression totale
              </AlertDialogTitle>
              <AlertDialogDescription className="space-y-4">
                <p>
                  Cette action va supprimer <strong>TOUTES</strong> les données de test de la base de données:
                </p>
                <ul className="list-disc list-inside text-sm space-y-1">
                  <li>Tous les dossiers</li>
                  <li>Tous les détenus</li>
                  <li>Tous les documents</li>
                  <li>Tous les paiements</li>
                  <li>Toutes les notifications</li>
                  <li>Toutes les alertes</li>
                  <li>Tous les procès</li>
                  <li>Tous les partages d&apos;accès</li>
                  <li>Toutes les réquisitions</li>
                </ul>
                <p className="font-semibold">
                  Pour confirmer, tapez: <code className="bg-muted px-2 py-1 rounded">EFFACER_TOUTES_LES_DONNEES</code>
                </p>
                <Input
                  value={confirmationText}
                  onChange={(e) => setConfirmationText(e.target.value)}
                  placeholder="Tapez la confirmation..."
                  className="font-mono"
                  data-testid="confirm-clear-all-input"
                />
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={() => setConfirmationText('')}>
                Annuler
              </AlertDialogCancel>
              <AlertDialogAction 
                onClick={handleClearAll}
                disabled={confirmationText !== 'EFFACER_TOUTES_LES_DONNEES' || loading}
                className="bg-destructive hover:bg-destructive/90"
              >
                {loading ? 'Suppression...' : 'Effacer tout'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Clear Collection Dialog */}
        <AlertDialog open={showClearCollectionDialog} onOpenChange={setShowClearCollectionDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2 text-destructive">
                <AlertTriangle className="w-5 h-5" />
                Effacer la collection &quot;{selectedCollection}&quot;
              </AlertDialogTitle>
              <AlertDialogDescription>
                Cette action va supprimer tous les documents de la collection <strong>{selectedCollection}</strong>.
                Cette opération est irréversible.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Annuler</AlertDialogCancel>
              <AlertDialogAction 
                onClick={handleClearCollection}
                disabled={loading}
                className="bg-destructive hover:bg-destructive/90"
              >
                {loading ? 'Suppression...' : 'Effacer'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </DashboardLayout>
  );
};

export default AdminDataManagementPage;
